import { resolveLocalization } from '@portal-front/i18n/core';
import type { Localization, LocalizationSources } from '@portal-front/i18n/core';
import type { FeatureFlagsConfig, FeatureFlagsContextValues } from '../types/feature-flags.types';

/**
 * Condition property carrying the **market** — `pl`, `cz`, `sk`, `ro`, `hu`, `bg`.
 */
export const LOCALE_CONTEXT_KEY = 'locale';

/**
 * Condition property carrying the **language** — `pl`, `cs`, `sk`, `ro`, `hu`, `bg`, `uk`.
 *
 * Distinct from the market: the Polish market read in Ukrainian is
 * `{ locale: 'pl', language: 'uk' }`, and a flag can condition on either half.
 */
export const LANGUAGE_CONTEXT_KEY = 'language';

export type CreateFeatureFlagsConfigOptions = {
  /** `app_name` in the feature_flag table, for example `portal-front` or `studio-cards`. */
  app: string;
  /** Base API URL, without a trailing path. */
  apiUrl: string;
} & Omit<Partial<FeatureFlagsConfig>, 'apiUrl'>;

export function createFeatureFlagsConfig({
  app,
  apiUrl,
  ...overrides
}: CreateFeatureFlagsConfigOptions): FeatureFlagsConfig {
  return {
    apiUrl: `${apiUrl.replace(/\/$/, '')}/application/${app}/feature-flags`,
    defaultFlags: {},
    ...overrides,
  };
}

/**
 * The context every application starts from.
 *
 * `locale`/`language` only — this is a portal-front-only concern. The PHP portal's
 * `FeatureFlagService::enabled` fills its own context (`localization`) independently and is not
 * affected by anything here; a flag evaluated on both sides needs a condition on each side's own
 * property, not a shared one.
 */
export function buildDefaultContext(sources: LocalizationSources): FeatureFlagsContextValues {
  return contextFromLocalization(resolveLocalization(sources));
}

/**
 * Same shape as `buildDefaultContext`, for a caller that already resolved the pair — a widget
 * reading it out of its SSR payload rather than re-deriving it from the browser.
 */
export function contextFromLocalization({ locale, language }: Localization): FeatureFlagsContextValues {
  return {
    [LOCALE_CONTEXT_KEY]: locale,
    [LANGUAGE_CONTEXT_KEY]: language,
  };
}
